import type { IConsumable } from '@experiences/interfaces';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import {
    Border,
    FontVariantToken,
    Spacing,
    Typography,
} from '@uipath/apollo-core';
import {
    ApTypography,
    PortalErrorPage,
} from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useMemo,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useOrganizationName } from '../../../../common/hooks/useOrganizationName';
import { accountGlobalId } from '../../../../store/selectors';
import { TenantUsageGrid } from '../../Consumables/TenantUsageGrid';
import { ConsumptionAnalytics } from '../../consumptionDashboard/components/ConsumptionAnalytics';
import { ConsumptionAnalyticsExportDialog } from '../../consumptionDashboard/components/ConsumptionAnalyticsExportDialog';
import { ConsumptionDateRangePicker } from '../../consumptionDashboard/components/ConsumptionDateRangePicker';
import { flatActionButtonStyle } from '../../consumptionDashboard/components/flatActionButtonStyle';
import { useConsumptionTimezone } from '../../consumptionDashboard/hooks/useConsumptionTimezone';
import {
    epochToZonedWallClock,
    getRangeUnixSeconds,
} from '../../consumptionDashboard/TenantLevelDashboard.utils';
import {
    formatPeriod,
    getUnitTypeDisplayName,
    VALID_UNIT_TYPES,
} from '../../consumptionDashboard/utils';
import { getErrorStatusCode } from '../../freeConsumablesV2/helpers';
import { getConsumableBundleRange } from './consumableBundleRange';
import { useConsumableOverviewData } from './useConsumableOverviewData';
import {
    toUtcDay,
    useUtcToday,
} from './useUtcToday';

const useStyles = makeStyles(theme =>
    createStyles({
        panel: {
            border: `1px solid ${theme.palette.semantic.colorPrimary}`,
            borderRadius: Border.BorderRadiusL,
            padding: `${Spacing.SpacingL} ${Spacing.SpacingM} ${Spacing.SpacingM}`,
        },
        overviewHeader: {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: Spacing.SpacingBase,
            marginBottom: Spacing.SpacingBase,
        },
        actionButton: flatActionButtonStyle(theme),
        allocationButton: { marginTop: Spacing.SpacingS },
        dateRangeWrapper: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: Spacing.SpacingMicro,
        },
    }),
);

export const OverviewPanel: React.FC<{ consumable: IConsumable; tenantId?: string; tenantName?: string }> = ({
    consumable, tenantId, tenantName,
}) => {
    const classes = useStyles();
    const {
        formatMessage: translate, locale,
    } = useIntl();
    const accountId = useSelector(accountGlobalId);
    const organizationName = useOrganizationName();
    const { timezoneId } = useConsumptionTimezone();
    const [ showTenantAllocation, setShowTenantAllocation ] = useState(false);
    const [ exportDialogOpen, setExportDialogOpen ] = useState(false);
    const [ selectedRange, setSelectedRange ] = useState<{ start: string; end: string } | null>(null);

    const unitTypeDisplay = useMemo(
        () => getUnitTypeDisplayName(consumable.code, translate),
        [ consumable.code, translate ],
    );

    const {
        bundleStartDate, bundleEndDate,
    } = useMemo(() => getConsumableBundleRange(consumable), [ consumable ]);

    const isOrgLevel = tenantId == null;
    // Tenant allocation reuses the LRM allocations grid, which only covers the dashboard's unit types.
    const canShowTenantAllocation = isOrgLevel
        && VALID_UNIT_TYPES.includes(consumable.code as typeof VALID_UNIT_TYPES[number]);
    const {
        distributionData, isLoading, error,
    } = useConsumableOverviewData(accountId, consumable.code, tenantId, bundleStartDate, bundleEndDate);

    const bundleStartDateStr = useMemo(
        () => bundleStartDate != null ? epochToZonedWallClock(bundleStartDate, timezoneId) : null,
        [ bundleStartDate, timezoneId ],
    );
    const bundleEndDateStr = useMemo(
        () => bundleEndDate != null ? epochToZonedWallClock(bundleEndDate, timezoneId) : null,
        [ bundleEndDate, timezoneId ],
    );

    const periodDisplay = useMemo(
        () => bundleStartDate != null && bundleEndDate != null
            ? formatPeriod(bundleStartDate, bundleEndDate, locale, timezoneId)
            : '',
        [ bundleStartDate, bundleEndDate, locale, timezoneId ],
    );

    // The picker is bounded to the bundle (pastThreshold below), but presets can still hand back a
    // start before it — clamp, since nothing outside the bundle was ever fetched.
    const handleDateRangeChange = useCallback((start: string, end: string) => {
        const day = (value: string) => value.slice(0, 10);
        setSelectedRange({
            start: bundleStartDateStr && day(start) < day(bundleStartDateStr) ? bundleStartDateStr : start,
            end: bundleEndDateStr && day(end) > day(bundleEndDateStr) ? bundleEndDateStr : end,
        });
    }, [ bundleStartDateStr, bundleEndDateStr ]);
    const handleReset = useCallback(() => setSelectedRange(null), []);

    // The fetch always pulls the whole bundle in one shot — the range picker and "today" clamp
    // below just slice that cached dataset, never triggering a new query.
    const effectiveRangeUnix = useMemo(() => {
        if (selectedRange) {
            return getRangeUnixSeconds(selectedRange, timezoneId);
        }
        return bundleStartDate != null && bundleEndDate != null
            ? {
                startDate: bundleStartDate,
                endDate: bundleEndDate,
            }
            : null;
    }, [ selectedRange, bundleStartDate, bundleEndDate, timezoneId ]);

    // The lease endpoint keys days in UTC, so slice on calendar days without the dashboard
    // timezone: picked days exactly as picked, bundle bounds and "today" as UTC days. Going
    // through `timezoneId` here would shift the edges by a day for users far from UTC.
    const displayedDayRange = useMemo(() => {
        if (!effectiveRangeUnix) {
            return null;
        }
        if (selectedRange) {
            return {
                start: selectedRange.start.slice(0, 10),
                end: selectedRange.end.slice(0, 10),
            };
        }
        return {
            start: toUtcDay(effectiveRangeUnix.startDate),
            end: toUtcDay(effectiveRangeUnix.endDate),
        };
    }, [ effectiveRangeUnix, selectedRange ]);

    const todayStr = useUtcToday();

    const displayedDistribution = useMemo(() => {
        if (!displayedDayRange) {
            return [];
        }
        const clampedEnd = displayedDayRange.end < todayStr ? displayedDayRange.end : todayStr;
        return distributionData.filter(item => item.date >= displayedDayRange.start && item.date <= clampedEnd);
    }, [ distributionData, displayedDayRange, todayStr ]);

    // The export must describe the same window as the rows it contains — never a range past today.
    const exportRangeUnix = useMemo(() => {
        if (!effectiveRangeUnix) {
            return undefined;
        }
        const endOfTodayUnix = Math.floor(Date.parse(`${todayStr}T23:59:59Z`) / 1000);
        return {
            startDate: effectiveRangeUnix.startDate,
            endDate: Math.min(effectiveRangeUnix.endDate, endOfTodayUnix),
        };
    }, [ effectiveRangeUnix, todayStr ]);

    const displayedServices = useMemo(() => {
        const totals = new Map<string, number>();
        displayedDistribution.forEach(day => day.services.forEach(service => {
            totals.set(service.service, (totals.get(service.service) ?? 0) + service.consumedAmount);
        }));
        return [ ...totals.entries() ].map(([ service, consumedAmount ]) => ({
            service,
            consumedAmount,
        }));
    }, [ displayedDistribution ]);

    const hasAnyData = !isLoading && (
        displayedDistribution.some(item => item.totalConsumedAmount > 0)
        || displayedServices.some(item => item.consumedAmount > 0)
    );

    if (error) {
        return (
            <div
                className={classes.panel}
                data-cy={`consumables-v2-overview-panel-${consumable.code.toLowerCase()}`}>
                <PortalErrorPage
                    type={getErrorStatusCode(error)}
                    buttonText={translate({ id: 'CLIENT_TRY_AGAIN' })}
                    buttonHref={window.location.href} />
            </div>
        );
    }

    return (
        <div
            className={classes.panel}
            data-cy={`consumables-v2-overview-panel-${consumable.code.toLowerCase()}`}>
            <div className={classes.overviewHeader}>
                <button
                    type="button"
                    className={classes.actionButton}
                    data-cy={`consumables-v2-overview-export-${consumable.code.toLowerCase()}`}
                    disabled={!hasAnyData}
                    onClick={() => setExportDialogOpen(true)}
                >
                    <DownloadOutlinedIcon sx={{ fontSize: Typography.fontSizeM }} />
                    <ApTypography variant={FontVariantToken.fontSizeSBold}>
                        {translate({ id: 'CLIENT_CUSTOM_EXPORT' })}
                    </ApTypography>
                </button>
                {bundleStartDateStr != null && bundleEndDateStr != null && (
                    <div className={classes.dateRangeWrapper}>
                        <ConsumptionDateRangePicker
                            bundleStartDate={bundleStartDateStr}
                            bundleEndDate={bundleEndDateStr}
                            periodDisplay={periodDisplay}
                            initialStartDate={selectedRange?.start}
                            initialEndDate={selectedRange?.end}
                            pastThreshold={bundleStartDateStr?.slice(0, 10)}
                            onDateRangeChange={handleDateRangeChange}
                            onReset={handleReset} />
                    </div>
                )}
            </div>

            <ConsumptionAnalyticsExportDialog
                open={exportDialogOpen}
                distributionData={displayedDistribution}
                organizationName={organizationName}
                tenantName={tenantName ?? ''}
                omitUnscopedColumns
                unitTypeDisplay={unitTypeDisplay}
                startDateUnix={exportRangeUnix?.startDate}
                endDateUnix={exportRangeUnix?.endDate}
                onClose={() => setExportDialogOpen(false)}
                onExportTelemetry={() => { /* no telemetry yet */ }} />

            <ConsumptionAnalytics
                hideHeader
                omitUnscopedColumns
                unitTypeDisplay={unitTypeDisplay}
                distributionData={displayedDistribution}
                servicesData={displayedServices}
                isLoading={isLoading}
                organizationName={organizationName}
                tenantName={tenantName ?? ''}
                startDateUnix={exportRangeUnix?.startDate}
                endDateUnix={exportRangeUnix?.endDate}
                onExportStarted={() => { /* no telemetry yet */ }} />

            {canShowTenantAllocation && (
                <>
                    <button
                        type="button"
                        className={`${classes.actionButton} ${classes.allocationButton}`}
                        onClick={() => setShowTenantAllocation(previous => !previous)}
                        data-cy={`consumables-v2-tenant-allocation-toggle-${consumable.code.toLowerCase()}`}
                    >
                        <ApTypography variant={FontVariantToken.fontSizeSBold}>
                            {translate({
                                id: showTenantAllocation
                                    ? 'CLIENT_CONSUMABLE_V2_HIDE_TENANT_ALLOCATION'
                                    : 'CLIENT_CONSUMABLE_V2_SHOW_TENANT_ALLOCATION',
                            })}
                        </ApTypography>
                    </button>
                    {showTenantAllocation && (
                        <TenantUsageGrid
                            consumableCode={consumable.code}
                            startDate={effectiveRangeUnix?.startDate}
                            endDate={effectiveRangeUnix?.endDate} />
                    )}
                </>
            )}
        </div>
    );
};
